import { ReactNode, useMemo, useState } from "react"
import {
    Button,
    CircularProgress,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Popover,
    Tooltip,
    Typography,
    Box,
} from "@mui/material"
import { FontAwesomeIcon, FontAwesomeIconProps } from "@fortawesome/react-fontawesome"
import { faCircleXmark } from "@fortawesome/pro-thin-svg-icons"
import { faCheck } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { useAppLocalization } from "../../../modules/localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../modules/localization/components/AppLocalized"

const useStyles = makeStyles()((theme) => ({
    floating: {
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "55px",
        flexWrap: "nowrap",
        zIndex: theme.zIndex.drawer + 1,
    },
    itemContainer: {
        justifyContent: "center",
        alignItems: "center",
        width: "5rem",
        height: "100%",
        paddingTop: "0.5rem",
        paddingBottom: "0.5rem",
        flexDirection: "column",
        flexWrap: "nowrap",
    },
    itemTitle: {
        whiteSpace: "nowrap",
    },
    number: {
        color: "white",
        background: "#2196F3",
        borderTopLeftRadius: ".5rem",
        borderBottomLeftRadius: ".5rem",
    },
    iconColor: {
        color: "#2196f3",
    },
    popupWrapper: {
        borderRadius: ".5rem",
    },
    rounded: {
        borderTopRightRadius: ".5rem",
        borderBottomRightRadius: ".5rem",
    },
    popupPaper: {
        borderRadius: "10px",
    },
    menuItem: {
        padding: "0.7rem",
        fontSize: "0.875rem",
        color: theme.palette.tailwind.slate[600],
    },
    button: {
        color: theme.palette.tailwind.slate[600],
        textTransform: "none",
        fontWeight: "400",
        fontSize: "0.875rem",
    },
    itemLoading: {
        backgroundColor: theme.palette.tailwind.sky[50],
        borderRadius: ".5rem",
        "&.Mui-disabled": {
            backgroundColor: theme.palette.tailwind.sky[50],
        },
    },
    itemSuccess: {
        backgroundColor: theme.palette.tailwind.green[50],
        borderRadius: ".5rem",
        "&.Mui-disabled": {
            backgroundColor: theme.palette.tailwind.green[50],
        },
    },
    iconSlot: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "1.5rem",
        width: "1.5rem",
    },
    spinner: {
        color: theme.palette.tailwind.sky[600],
    },
    successIcon: {
        color: theme.palette.tailwind.green[600],
    },
    successTitle: {
        color: theme.palette.tailwind.green[700],
    },
    actionBarMenu: {
        "@media print": {
            display: "none",
        },
    },
    modalContainer: {
        paddingBottom: theme.spacing(12),
        marginTop: "-1rem",
    },
}))

type FloatingActionBarProps = {
    selectedItemsLength: number
    children: ReactNode
    handleCloseActionBar: () => void
}

export const FloatingActionBar = ({ selectedItemsLength, children, handleCloseActionBar }: FloatingActionBarProps) => {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()

    return (
        <div className={classes.floating}>
            <Paper elevation={12} sx={{ minWidth: "746px" }} className={classes.popupWrapper}>
                <Grid container gap={1.5} justifyContent="space-between" wrap="nowrap" className={classes.rounded}>
                    <Grid container item gap={1.5} wrap="nowrap">
                        <Grid container item className={cx(classes.itemContainer, classes.number)}>
                            <Typography variant="h4">
                                {selectedItemsLength > 99 ? "99+" : selectedItemsLength}
                            </Typography>
                        </Grid>
                        <Grid container item className={cx(classes.itemContainer)}>
                            <LocalizedStrict id="floating-action-button-picked">
                                <Typography color="GrayText" lineHeight="1.3rem">
                                    Selected items
                                </Typography>
                            </LocalizedStrict>
                        </Grid>
                    </Grid>
                    <Grid container item gap={1.5} justifyContent="end" wrap="nowrap">
                        {children}
                        <Tooltip title={l10n.getString("floating-action-button")}>
                            <Grid
                                alignContent={"center"}
                                container
                                item
                                className={cx(classes.itemContainer)}
                                sx={{ borderLeft: "1px solid #C0C0C0" }}
                            >
                                <IconButton sx={{ fontSize: "1rem" }} onClick={handleCloseActionBar}>
                                    <FontAwesomeIcon icon={faCircleXmark} size="xl" style={{ color: "#94a3b8" }} />
                                </IconButton>
                            </Grid>
                        </Tooltip>
                    </Grid>
                </Grid>
            </Paper>
        </div>
    )
}

type ActionBarItemProps = {
    icon: FontAwesomeIconProps["icon"]
    title: string
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
    containerStyle?: string
    isLoading?: boolean
    isSuccess?: boolean
    disabled?: boolean
}

export const ActionBarItem = ({
    icon,
    title,
    onClick,
    containerStyle,
    isLoading = false,
    isSuccess = false,
    disabled = false,
}: ActionBarItemProps) => {
    const { classes, cx } = useStyles()

    const renderedIcon = useMemo(() => {
        if (isLoading) {
            return <CircularProgress size="1.25rem" className={classes.spinner} />
        }
        if (isSuccess) {
            return <FontAwesomeIcon icon={faCheck} size="xl" className={classes.successIcon} />
        }
        return <FontAwesomeIcon icon={icon} size="xl" className={classes.iconColor} />
    }, [isLoading, isSuccess, icon, classes])

    return (
        <Button
            onClick={onClick}
            disabled={disabled || isLoading || isSuccess}
            className={cx(classes.button, isLoading && classes.itemLoading, isSuccess && classes.itemSuccess)}
        >
            <Grid container item direction={"column"} className={cx(classes.itemContainer, containerStyle)}>
                <span className={classes.iconSlot}>{renderedIcon}</span>
                <span className={cx(classes.itemTitle, isSuccess && classes.successTitle)}>{title}</span>
            </Grid>
        </Button>
    )
}

type ActionBarMenuProps = {
    icon: FontAwesomeIconProps["icon"]
    title: string
    children: ReactNode
    isLoading?: boolean
}

export const ActionBarMenu = ({ icon, title, children, isLoading = false }: ActionBarMenuProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const { classes } = useStyles()
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }
    const handleClose = () => {
        setAnchorEl(null)
    }

    // Close the menu while its action runs so the loading spinner on the trigger is visible.
    const open = Boolean(anchorEl) && !isLoading

    return (
        <>
            <ActionBarItem icon={icon} title={title} onClick={handleClick} isLoading={isLoading} />
            <Popover
                disableScrollLock
                open={open}
                className={classes.actionBarMenu}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
                <Box sx={{ padding: "1rem" }}>{children}</Box>
            </Popover>
        </>
    )
}

type ActionBarMenuItemProps = {
    icon?: FontAwesomeIconProps["icon"]
    title: string
    onClick: () => void
    disabled?: boolean
}

export const ActionBarMenuItem = ({ icon, title, onClick, disabled = false }: ActionBarMenuItemProps) => {
    const { classes } = useStyles()
    return (
        <MenuItem disabled={disabled} onClick={onClick} className={classes.menuItem}>
            {icon && (
                <FontAwesomeIcon
                    icon={icon}
                    fixedWidth
                    size="xl"
                    className={classes.iconColor}
                    style={{ paddingRight: "0.5rem" }}
                />
            )}
            {title}
        </MenuItem>
    )
}

// EXAMPLE for use
// {selectedRows && (
//     <FloatingActionBar
//         selectedItemsLength={selectedRows.length}
//         handleCloseActionBar={() => setSelectedRows('CLEAR SELECTED OR ...')}
//     >
//         <LocalizedStrict id="id" attrs={{ title: true }}>
//             <ActionBarItem
//                 icon={someIcon}
//                 title="ITEM TITLE"
//                 onClick={() => {}}
//             />
//         </LocalizedStrict>

//         <LocalizedStrict id="id" attrs={{ title: true }}>
//             <ActionBarMenu icon={faFileArrowDown} title="MENU TITLE">
//                 <LocalizedStrict id="id" attrs={{ title: true }}>
//                     <ActionBarMenuItem
//                         icon={someIcon}
//                         title={"MENU ITEM TITLE"}
//                         onClick={() => {}}
//                     />
//                 </LocalizedStrict>
//                 <LocalizedStrict id="id" attrs={{ title: true }}>
//                     <ActionBarMenuItem
//                         icon={someIcon}
//                         title={"MENU ITEM TITLE"}
//                         onClick={() => {}}
//                         disabled={true}
//                     />
//                 </LocalizedStrict>
//             </ActionBarMenu>
//         </LocalizedStrict>
//     </FloatingActionBar>
// )}
